import { isPlatformBrowser } from "@angular/common";
import { Component, computed, DestroyRef, inject, OnInit, PLATFORM_ID } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { filter, switchMap, tap } from "rxjs";

import { NgOptimizedImage } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { HotToastService } from "@ngxpert/hot-toast";
import { isDefined, isPropertyDefined } from "@recursyve/nice-ts-utils";
import { SvgIconComponent } from "angular-svg-icon";
import { DirectusTranslationPipe } from "../../../../common/pipes/directus-translation.pipe";
import { GetDirectusImagePipe } from "../../../../common/pipes/get-directus-image.pipe";
import { PhoneFormatPipe } from "../../../../common/pipes/phone-format.pipe";
import { UrlFormatPipe } from "../../../../common/pipes/url-format.pipe";
import {
    sanitizeDirectusArray,
    sanitizeDirectusObject,
    sanitizeDirectusRelationArray
} from "../../../../common/utils/sanitize-directus-object";
import { Files } from "../../../api/packbo/models/files";
import { PackageBadgeComponent } from "../../../components/package-badge/package-badge.component";
import { SeoService } from "../../../services/seo.service";
import { SupplierFilesComponent } from "./components/supplier-files/supplier-files.component";
import { SupplierMapComponent } from "./components/supplier-map/supplier-map.component";
import { SupplierService } from "./store/supplier.service";
import { SupplierStore } from "./store/supplier.store";

@Component({
    selector: "packbo-supplier",
    templateUrl: "./supplier.template.html",
    providers: [SupplierService, SupplierStore],
    imports: [
        SvgIconComponent,
        GetDirectusImagePipe,
        DirectusTranslationPipe,
        TranslocoPipe,
        PackageBadgeComponent,
        NgOptimizedImage,
        SupplierMapComponent,
        PhoneFormatPipe,
        UrlFormatPipe,
        SupplierFilesComponent
    ]
})
export class SupplierComponent implements OnInit {
    private readonly supplierService = inject(SupplierService);
    private readonly supplierStore = inject(SupplierStore);
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly toast = inject(HotToastService);
    private readonly translocoService = inject(TranslocoService);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly seoService = inject(SeoService);
    public readonly supplier = this.supplierStore.supplier;

    public readonly package = computed(() => sanitizeDirectusObject(this.supplier()?.package_ref));
    public readonly productCategories = computed(() => {
        const productCategoriesRefs = this.supplier()?.product_categories_refs;
        if (!productCategoriesRefs) {
            return [];
        }

        return sanitizeDirectusRelationArray(productCategoriesRefs, "product_categories_id")
            .map((item) => sanitizeDirectusObject(item))
            .filter(isDefined);
    });
    public readonly certifications = computed(() => {
        const certificationsRefs = this.supplier()?.certifications_refs;
        if (!certificationsRefs) {
            return [];
        }

        return sanitizeDirectusRelationArray(certificationsRefs, "certifications_id")
            .map((item) => sanitizeDirectusObject(item))
            .filter(isDefined);
    });
    public readonly supplierCoordinates = computed(() => {
        const coordRef = this.supplier()?.coord_ref;
        if (!coordRef) {
            return null;
        }
        return sanitizeDirectusObject(coordRef);
    });
    public readonly supplierContact = computed(() => {
        const supplierContact = this.supplier()?.contact_ref;
        if (!supplierContact) {
            return null;
        }
        return sanitizeDirectusObject(supplierContact);
    });
    public readonly supplierFiles = computed(() => {
        const filesRefs = this.supplier()?.files_refs;
        if (!filesRefs || !isDefined(filesRefs)) {
            return [];
        }
        return sanitizeDirectusRelationArray(filesRefs, "directus_files_id").filter(
            (file): file is Files => typeof file === "object" && isDefined(file)
        );
    });
    public readonly imageData = computed(() => {
        const packageData = this.package();
        const categories = this.productCategories();

        if (packageData?.hide_supplier_package && categories.length > 0 && categories[0].image) {
            return {
                src: categories[0].image,
                alt: categories[0].translations_refs
            };
        }

        if (this.supplier()?.logo) {
            return {
                src: this.supplier()?.logo,
                alt: this.supplier()?.name
            };
        }

        return null;
    });

    public ngOnInit(): void {
        this.route.params
            .pipe(
                filter((params) => isPropertyDefined(params, "slug")),
                switchMap(({ slug }: Params) => this.supplierService.loadSupplier(slug)),
                tap(() => this.updateSeoTags()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.translocoService.langChanges$
            .pipe(
                tap(() => this.updateSeoTags()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    protected copyLink(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        navigator.clipboard.writeText(window.location.href);
        this.toast.success(this.translocoService.translate("supplier.link_copied"));
    }

    protected downloadPdf(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        window.print();
    }

    private updateSeoTags(): void {
        const supplier = this.supplier();
        if (supplier) {
            const translations = sanitizeDirectusArray(supplier.translations_refs).map((t) => ({
                languages_code: t.languages_code,
                title: supplier.name,
                meta_description: t.summary,
                slug: t.slug
            }));

            this.seoService.updateMetaTags(
                {
                    translations,
                    noIndex: false,
                    noFollow: false,
                    isPrivate: false
                },
                this.router.url
            );
        }
    }
}
